import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { adminAPI } from '../services/api';
import { getDrugLabel } from '../constants/drugs';
import type { AdminModelMeta } from '../types';
import { formatErrorMessage } from '../utils/errorHandler';
import {
  CompactStatusGlyph,
  CompactStatusLegend,
  resolveCompactStatusTone,
} from './CompactStatus';
import { getCovariateTagStyle } from '../utils/clinicalTagPalette';

const { Text } = Typography;

export interface ModelScriptReport {
  id: string;
  model_id: string;
  script_file_base?: string;
  status: string;
  checks?: Record<string, unknown>;
  required_covariates?: string[];
  warnings?: unknown;
  errors?: unknown;
  duration_ms?: number;
  created_at?: string;
}

export interface ValidationModelFilters {
  keyword?: string;
  drugName?: string;
  qualificationStatus?: string;
  scriptStatus?: string;
}

export interface ScriptHealthModel {
  model_id: string;
  display_name?: string;
  drug?: string;
  drug_name?: string;
  drug_label?: string;
  inferred_drug_name?: string;
  script_file_base?: string;
  first_author?: string;
  publication_year?: number;
  qualification_status?: string;
}

export const filterValidationModels = <T extends ScriptHealthModel>(
  sourceModels: T[],
  sourceReports: ModelScriptReport[],
  filters: ValidationModelFilters,
): T[] => {
  const keyword = String(filters.keyword || '').trim().toLocaleLowerCase();
  const reportMap = new Map<string, ModelScriptReport>();
  sourceReports.forEach((report) => {
    if (!reportMap.has(report.model_id)) reportMap.set(report.model_id, report);
  });
  return sourceModels.filter((model) => {
    const drugName = model.drug_name || model.drug || model.inferred_drug_name || '';
    const drugLabel = model.drug_label || getDrugLabel(drugName);
    const searchable = [
      model.model_id,
      model.display_name,
      model.first_author,
      model.publication_year,
      model.script_file_base,
      drugName,
      drugLabel,
    ].map((value) => String(value || '').toLocaleLowerCase());
    if (keyword && !searchable.some((value) => value.includes(keyword))) return false;
    if (filters.drugName && drugName !== filters.drugName) return false;
    if (
      filters.qualificationStatus
      && String(model.qualification_status || 'not_started') !== filters.qualificationStatus
    ) return false;
    const scriptStatus = reportMap.get(model.model_id)?.status || 'not_checked';
    if (filters.scriptStatus && scriptStatus !== filters.scriptStatus) return false;
    return true;
  });
};

const safeArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined).map((item) => {
      const text = typeof item === 'string' ? item : JSON.stringify(item);
      return text || String(item);
    });
  }
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'string') return [value];
  return [JSON.stringify(value)];
};

const renderCovariates = (items?: string[]) => {
  if (!items?.length) return <Text type="secondary">未检查</Text>;
  return (
    <Space size={[4, 4]} wrap>
      {items.map((item) => (
        <Tag key={item} className="clinical-entity-tag" style={getCovariateTagStyle(item)}>
          {item}
        </Tag>
      ))}
    </Space>
  );
};

interface ModelScriptHealthPanelProps {
  models: AdminModelMeta[];
  modelsLoading?: boolean;
  refreshToken?: number;
  onModelsChanged?: () => Promise<void> | void;
}

const ModelScriptHealthPanel: React.FC<ModelScriptHealthPanelProps> = ({
  models,
  modelsLoading = false,
  refreshToken,
  onModelsChanged,
}) => {
  const [reports, setReports] = useState<ModelScriptReport[]>([]);
  const [drugOptions, setDrugOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [checkingModelId, setCheckingModelId] = useState<string>();
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);
  const [keyword, setKeyword] = useState('');
  const [drugFilter, setDrugFilter] = useState<string>();
  const [qualificationFilter, setQualificationFilter] = useState<string>();
  const [scriptStatusFilter, setScriptStatusFilter] = useState<string>();
  const [batchChecking, setBatchChecking] = useState(false);
  const [updatingDrugModelId, setUpdatingDrugModelId] = useState<string>();

  const loadPanelData = useCallback(async () => {
    setLoading(true);
    const [reportResult, drugResult] = await Promise.allSettled([
      adminAPI.listValidationScriptReports({ latest_only: true }),
      adminAPI.listDrugParams(),
    ]);
    if (reportResult.status === 'fulfilled') {
      setReports(Array.isArray(reportResult.value?.items) ? reportResult.value.items : []);
    }
    if (drugResult.status === 'fulfilled') {
      const raw = drugResult.value;
      const rows = Array.isArray(raw) ? raw : raw?.items || raw?.drugs || [];
      const seen = new Set<string>();
      const nextOptions: Array<{ value: string; label: string }> = [];
      rows.forEach((drug: Record<string, unknown>) => {
        const value = String(drug.drug_id || drug.drug_name || drug.name || '').trim();
        if (!value || seen.has(value.toLowerCase())) return;
        seen.add(value.toLowerCase());
        nextOptions.push({
          value,
          label: String(drug.display_name || drug.drug_name || getDrugLabel(value) || value),
        });
      });
      setDrugOptions(nextOptions);
    }
    if (reportResult.status === 'rejected' || drugResult.status === 'rejected') {
      message.warning('脚本健康数据部分加载失败，请刷新重试');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadPanelData();
  }, [loadPanelData, refreshToken]);

  const reportByModel = useMemo(() => {
    const map = new Map<string, ModelScriptReport>();
    reports.forEach((report) => {
      if (!map.has(report.model_id)) map.set(report.model_id, report);
    });
    return map;
  }, [reports]);

  const filteredModels = useMemo(() => filterValidationModels(models, reports, {
    keyword,
    drugName: drugFilter,
    qualificationStatus: qualificationFilter,
    scriptStatus: scriptStatusFilter,
  }), [drugFilter, keyword, models, qualificationFilter, reports, scriptStatusFilter]);

  const selectedModels = useMemo(
    () => models.filter((model) => selectedModelIds.includes(model.model_id)),
    [models, selectedModelIds],
  );

  useEffect(() => {
    const visibleIds = new Set(filteredModels.map((model) => model.model_id));
    setSelectedModelIds((current) => current.filter((modelId) => visibleIds.has(modelId)));
  }, [filteredModels]);

  const qualificationOptions = useMemo(() => Array.from(new Set(
    models.map((model) => String(model.qualification_status || 'not_started')),
  )).sort().map((value) => ({ value, label: value })), [models]);

  const scriptStatusOptions = useMemo(() => {
    const values = new Set(reports.map((report) => String(report.status || 'not_checked')));
    if (models.some((model) => !reportByModel.has(model.model_id))) values.add('not_checked');
    return Array.from(values).sort().map((value) => ({
      value,
      label: value === 'not_checked' ? '未检查' : value,
    }));
  }, [models, reportByModel, reports]);

  const runScriptCheck = async (model: AdminModelMeta) => {
    setCheckingModelId(model.model_id);
    try {
      const report = await adminAPI.runModelScriptValidation(model.model_id, model.script_file_base);
      message.success(report.status === 'passed' ? '脚本检查通过' : '脚本检查完成，请查看详情');
      await loadPanelData();
    } catch (error) {
      message.error(formatErrorMessage(error, '脚本检查失败'));
    } finally {
      setCheckingModelId(undefined);
    }
  };

  const runBatchCheck = async () => {
    if (!selectedModels.length) {
      message.warning('请先勾选需要检查的模型');
      return;
    }
    setBatchChecking(true);
    let passed = 0;
    let failed = 0;
    const messageKey = 'model-script-batch-check';
    try {
      for (let index = 0; index < selectedModels.length; index += 1) {
        const model = selectedModels[index];
        setCheckingModelId(model.model_id);
        message.open({
          key: messageKey,
          type: 'loading',
          content: `批量检查中 ${index + 1}/${selectedModels.length}: ${model.model_id}`,
          duration: 0,
        });
        try {
          const report = await adminAPI.runModelScriptValidation(model.model_id, model.script_file_base);
          if (report.status === 'passed') passed += 1;
          else failed += 1;
        } catch {
          failed += 1;
        }
      }
      message.open({
        key: messageKey,
        type: failed ? 'warning' : 'success',
        content: `批量检查完成：通过 ${passed} 个，需处理 ${failed} 个`,
        duration: 4,
      });
      setSelectedModelIds([]);
      await loadPanelData();
    } finally {
      setCheckingModelId(undefined);
      setBatchChecking(false);
    }
  };

  const updateDrugName = async (modelId: string, drugName: string) => {
    setUpdatingDrugModelId(modelId);
    try {
      await adminAPI.updateModelValidationDrugName(modelId, drugName);
      message.success('模型药物标签已更新');
      await onModelsChanged?.();
    } catch (error) {
      message.error(formatErrorMessage(error, '更新模型药物标签失败'));
    } finally {
      setUpdatingDrugModelId(undefined);
    }
  };

  const inferDrugName = async (modelId: string) => {
    setUpdatingDrugModelId(modelId);
    try {
      const result = await adminAPI.inferModelValidationDrugName(modelId);
      message.success(`已自动标注为 ${getDrugLabel(result.drug_name)}`);
      await onModelsChanged?.();
    } catch (error) {
      message.error(formatErrorMessage(error, '自动识别药物标签失败'));
    } finally {
      setUpdatingDrugModelId(undefined);
    }
  };

  const columns = [
    {
      title: '模型',
      dataIndex: 'model_id',
      width: 280,
      render: (value: string, record: AdminModelMeta) => (
        <Space direction="vertical" size={2} style={{ maxWidth: 260 }}>
          <Text strong ellipsis={{ tooltip: value }}>{record.display_name || value}</Text>
          <Text type="secondary" ellipsis={{ tooltip: value }}>{value}</Text>
          <Text type="secondary">{record.first_author || '-'} {record.publication_year || ''}</Text>
        </Space>
      ),
    },
    {
      title: '药物标签',
      width: 280,
      render: (_: unknown, record: AdminModelMeta) => {
        const current = record.drug_name || record.drug || undefined;
        const inferred = record.inferred_drug_name && record.inferred_drug_name !== current
          ? record.inferred_drug_name
          : undefined;
        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space.Compact style={{ width: '100%' }}>
              <Select
                size="small"
                showSearch
                placeholder="选择药物"
                value={current}
                loading={updatingDrugModelId === record.model_id}
                optionFilterProp="label"
                style={{ minWidth: 160, flex: 1 }}
                options={drugOptions}
                onChange={(value) => void updateDrugName(record.model_id, value)}
              />
              <Button
                size="small"
                icon={<EditOutlined />}
                loading={updatingDrugModelId === record.model_id}
                onClick={() => void inferDrugName(record.model_id)}
              >
                自动
              </Button>
            </Space.Compact>
            {inferred ? <Text type="secondary">推断：{getDrugLabel(inferred)}</Text> : null}
          </Space>
        );
      },
    },
    {
      title: '脚本基名',
      dataIndex: 'script_file_base',
      width: 220,
      render: (value?: string) => <Text code ellipsis={{ tooltip: value }}>{value || '-'}</Text>,
    },
    {
      title: '所需协变量',
      width: 260,
      render: (_: unknown, record: AdminModelMeta) => (
        renderCovariates(reportByModel.get(record.model_id)?.required_covariates)
      ),
    },
    {
      title: '最近检查',
      width: 190,
      render: (_: unknown, record: AdminModelMeta) => {
        const report = reportByModel.get(record.model_id);
        return report ? (
          <Space direction="vertical" size={0}>
            <CompactStatusGlyph
              label="脚本检查"
              value={report.status}
              tone={resolveCompactStatusTone(report.status)}
            />
            <Text type="secondary">{report.created_at || '-'}</Text>
          </Space>
        ) : <CompactStatusGlyph label="脚本检查" value="not_checked" tone="neutral" />;
      },
    },
    {
      title: '问题',
      width: 440,
      render: (_: unknown, record: AdminModelMeta) => {
        const report = reportByModel.get(record.model_id);
        const issues = [...safeArray(report?.errors), ...safeArray(report?.warnings)];
        return issues.length ? (
          <Text type={report?.status === 'failed' ? 'danger' : 'warning'}>
            {issues.slice(0, 2).join('; ')}
          </Text>
        ) : <Text type="secondary">-</Text>;
      },
    },
    {
      title: '操作',
      width: 128,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: unknown, record: AdminModelMeta) => (
        <Button
          icon={<CheckCircleOutlined />}
          loading={checkingModelId === record.model_id || batchChecking}
          onClick={() => void runScriptCheck(record)}
        >
          检查
        </Button>
      ),
    },
  ];

  return (
    <Card
      title="模型脚本健康"
      extra={(
        <Space>
          <Button
            icon={<CheckCircleOutlined />}
            disabled={!selectedModelIds.length}
            loading={batchChecking}
            onClick={() => void runBatchCheck()}
          >
            批量检查
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => void loadPanelData()} loading={loading}>
            刷新
          </Button>
        </Space>
      )}
    >
      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
        <Col xs={24} md={10} xl={8}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            aria-label="搜索模型脚本"
            placeholder="搜索模型 ID、展示名称、作者或脚本"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
          />
        </Col>
        <Col xs={24} sm={12} md={7} xl={5}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            aria-label="按药物筛选模型脚本"
            placeholder="全部药物"
            value={drugFilter}
            options={drugOptions}
            style={{ width: '100%' }}
            onChange={setDrugFilter}
          />
        </Col>
        <Col xs={24} sm={12} md={7} xl={4}>
          <Select
            allowClear
            aria-label="按技术资格筛选模型脚本"
            placeholder="全部技术资格"
            value={qualificationFilter}
            options={qualificationOptions}
            style={{ width: '100%' }}
            onChange={setQualificationFilter}
          />
        </Col>
        <Col xs={24} sm={12} md={7} xl={4}>
          <Select
            allowClear
            aria-label="按脚本检查状态筛选模型"
            placeholder="全部检查状态"
            value={scriptStatusFilter}
            options={scriptStatusOptions}
            style={{ width: '100%' }}
            onChange={setScriptStatusFilter}
          />
        </Col>
        <Col xs={24} sm={12} md={5} xl={3}>
          <Space size={8}>
            <Button
              icon={<ReloadOutlined />}
              disabled={!keyword && !drugFilter && !qualificationFilter && !scriptStatusFilter}
              onClick={() => {
                setKeyword('');
                setDrugFilter(undefined);
                setQualificationFilter(undefined);
                setScriptStatusFilter(undefined);
              }}
            >
              重置
            </Button>
            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
              {filteredModels.length}/{models.length}
            </Text>
          </Space>
        </Col>
      </Row>
      <Table<AdminModelMeta>
        className="compact-status-pagination"
        rowKey="model_id"
        loading={modelsLoading || loading || batchChecking}
        columns={columns}
        dataSource={filteredModels}
        rowSelection={{
          selectedRowKeys: selectedModelIds,
          onChange: (keys) => setSelectedModelIds(keys.map(String)),
        }}
        scroll={{ x: 1900 }}
        pagination={{
          showTotal: (total) => (
            <Space size={16} wrap>
              <CompactStatusLegend />
              <Text type="secondary">共 {total} 个模型</Text>
            </Space>
          ),
        }}
      />
    </Card>
  );
};

export default ModelScriptHealthPanel;
